import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "../../axios/axiosconf";
import { Link } from "react-router-dom";
import { normalizeFechaInput, formatearFechaDisplay, ymdLocal } from "../../../resources/Helper";
import { esConsumoActivo, esPlanActivo } from "../../../resources/planEstadoHelper";

/**
 * Panel Planes en FormNuevaCita: paciente, cita compacta, uno o varios planes y tabla de sesiones.
 */
function FormCitaPlanesPanel({
    modoGestion = false,
    soloPaciente = false,
    idPaciente,
    setIdPaciente,
    setIdPacienteExistente,
    primerNombre,
    setPrimerNombre,
    segundoNombre,
    setSegundoNombre,
    primerApellido,
    setPrimerApellido,
    segundoApellido,
    setSegundoApellido,
    tipoDocumento,
    setTipoDocumento,
    numeroDocumento,
    setNumeroDocumento,
    celular,
    setCelular,
    listaPacientes,
    planConsumo,
    setPlanConsumo,
    idCita,
    verCita,
    formSoloLectura,
    motivoSoloLecturaCita, 
    agendas,
    idUsers,
    setIdUsers,
    fechaCita,
    setFechaCita,
    horaAtencionCita,
    setHoraAtencionCita,
    minutoCita,
    setMinutoCita,
    ampmCita,
    setAmpmCita,
    estadoCita,
    handleEstadoCitaChange,
    estadosCita,
    duracionCita,
    setDuracionCita,
    stepDuracionCita,
    motivoConsulta,
    setMotivoConsulta,
    readOnlyEstadoCita,
    readOnlyProfesional,
    hayErrorCampo,
    textoError,
    errorlist,
}) {
    const [q, setQ] = useState("");
    const [planesPaciente, setPlanesPaciente] = useState([]);
    const [catalogo, setCatalogo] = useState([]);
    const [pacientePlanId, setPacientePlanId] = useState("");
    const [lineas, setLineas] = useState({});
    const [planNuevoId, setPlanNuevoId] = useState("");
    const [asignandoLocal, setAsignandoLocal] = useState(false);
    const [msg, setMsg] = useState("");
    const [loadingPlanes, setLoadingPlanes] = useState(false);
    const [consumoBloqueado, setConsumoBloqueado] = useState(false);
    const [consumoCargado, setConsumoCargado] = useState(false);
    const [consumoDetalle, setConsumoDetalle] = useState(null);
    const [consumoConsultaTerminada, setConsumoConsultaTerminada] = useState(false);
    const [usuariosProfesionales, setUsuariosProfesionales] = useState([]);
    /** Sesiones/profesional por plan: al cambiar de chip y volver, se restauran. */
    const lineasPorPlanRef = useRef({});
    /** Borradores independientes Planes ↔ Asignar planes. */
    const borradorPestanaRef = useRef({
        gestion: { pacientePlanId: '', lineas: {}, planConsumo: null, planNuevoId: '' },
        asignar: { pacientePlanId: '', lineas: {}, planConsumo: null, planNuevoId: '', planesPendientes: [] },
    });
    const modoPestanaRef = useRef(null);

    const nombreCompleto = [primerNombre, segundoNombre, primerApellido, segundoApellido].filter(Boolean).join(" ");
    const planesActivos = (planesPaciente || []).filter((b) => esPlanActivo(b.plan?.estado));
    const planSeleccionado = planesPaciente.find((b) => String(b.plan.id) === String(pacientePlanId));
    const deshabilitado = formSoloLectura || consumoBloqueado;

    /** Sesiones y profesional empiezan vacíos; los completa el usuario. */
    const initLineasDesdePlan = useCallback((detalle, prev = {}) => {
        const out = {};
        (detalle || []).forEach((d) => {
            const key = String(d.id);
            const prevRow = prev[key] || {};
            out[key] = {
                paciente_plan_detalle_id: d.id,
                lista_precios_id: d.lista_precios_id || undefined,
                cantidad: prevRow.cantidad !== undefined ? prevRow.cantidad : 0,
                id_users: prevRow.id_users !== undefined ? String(prevRow.id_users) : "",
            };
        });
        return out;
    }, []);

    const guardarLineasPlanActual = useCallback((planId, lineasActuales) => {
        if (!planId) return;
        lineasPorPlanRef.current[String(planId)] = lineasActuales || {};
    }, []);

    useEffect(() => {
        if (formSoloLectura) return;
        axios.get("/planes-agendamiento").then((r) => {
            setCatalogo((r.data || []).filter((p) => Number(p.activo) === 1));
        }).catch(() => setCatalogo([]));
    }, [formSoloLectura]);

    useEffect(() => {
        const userdata = localStorage.getItem("userdata");
        if (!userdata) return;
        const uid = JSON.parse(userdata).user_id;
        axios.get(`/users-by-client?uid=${uid}`)
            .then((r) => setUsuariosProfesionales(Array.isArray(r.data) ? r.data : []))
            .catch(() => setUsuariosProfesionales([]));
    }, []);

    useEffect(() => {
        setConsumoCargado(false);
        setConsumoConsultaTerminada(false);
        setConsumoDetalle(null);
    }, [idCita]);

    useEffect(() => {
        if (!formSoloLectura || !idCita) {
            setConsumoDetalle(null);
            setConsumoConsultaTerminada(false);
            return;
        }
        let cancelled = false;
        setConsumoConsultaTerminada(false);
        setConsumoDetalle(null);
        axios
            .get("/cita-plan-consumo", { params: { cita_id: idCita } })
            .then((r) => {
                if (!cancelled) setConsumoDetalle(r.data?.consumo || null);
            })
            .catch(() => {
                if (!cancelled) setConsumoDetalle(null);
            })
            .finally(() => {
                if (!cancelled) setConsumoConsultaTerminada(true);
            });
        return () => { cancelled = true; };
    }, [formSoloLectura, idCita]);

    useEffect(() => {
        if (!idPaciente) {
            setPlanesPaciente([]);
            setPacientePlanId("");
            setLineas({});
            lineasPorPlanRef.current = {};
            setPlanConsumo(null);
            setConsumoBloqueado(false);
            setConsumoCargado(false);
            return;
        }
        setLoadingPlanes(true);
        axios
            .get("/paciente-planes-agendamiento", { params: { paciente_id: idPaciente, todos: 1 } })
            .then((r) => {
                const lista = r.data || [];
                setPlanesPaciente((prev) => {
                    const pendientes = (prev || []).filter((b) => b.plan?._pendiente_asignar);
                    return pendientes.length ? [...pendientes, ...lista] : lista;
                });
                setLoadingPlanes(false);
            })
            .catch(() => {
                setPlanesPaciente((prev) => (prev || []).filter((b) => b.plan?._pendiente_asignar));
                setLoadingPlanes(false);
            });
    }, [idPaciente, setPlanConsumo]);

    useEffect(() => {
        if (!idCita || !idPaciente || consumoCargado) return;
        axios
            .get("/cita-plan-consumo", { params: { cita_id: idCita } })
            .then((r) => {
                const consumo = r.data?.consumo;
                if (consumo?.paciente_plan_id || (consumo?.lineas || []).length) {
                    const primerPlanId = consumo.paciente_plan_id
                        || consumo.paciente_plan_ids?.[0]
                        || (consumo.lineas || [])[0]?.paciente_plan_id;
                    if (primerPlanId) {
                        setPacientePlanId(String(primerPlanId));
                    }
                    setConsumoBloqueado(!!consumo.bloqueado);

                    const porPlan = {};
                    (consumo.lineas || []).forEach((ln) => {
                        const planKey = String(ln.paciente_plan_id || primerPlanId || '');
                        if (!planKey) return;
                        if (!porPlan[planKey]) porPlan[planKey] = {};
                        porPlan[planKey][String(ln.paciente_plan_detalle_id)] = {
                            paciente_plan_detalle_id: ln.paciente_plan_detalle_id,
                            lista_precios_id: ln.lista_precios_id || undefined,
                            cantidad: Number(ln.cantidad) || 0,
                            id_users: ln.id_users ? String(ln.id_users) : "",
                        };
                    });
                    Object.keys(porPlan).forEach((planKey) => {
                        lineasPorPlanRef.current[planKey] = {
                            ...(lineasPorPlanRef.current[planKey] || {}),
                            ...porPlan[planKey],
                        };
                    });
                    const activoKey = String(primerPlanId || '');
                    if (activoKey && porPlan[activoKey]) {
                        setLineas((prev) => ({ ...prev, ...porPlan[activoKey] }));
                    }
                }
                setConsumoCargado(true);
            })
            .catch(() => setConsumoCargado(true));
    }, [idCita, idPaciente, consumoCargado]);

    /** Con plan elegido + detalle cargado: completa filas sin perder cantidades/profesional ya puestos. */
    useEffect(() => {
        if (!pacientePlanId) return;
        const plan = planesPaciente.find((b) => String(b.plan?.id) === String(pacientePlanId));
        if (!plan?.detalle?.length) return;
        setLineas((prev) => {
            const cached = lineasPorPlanRef.current[String(pacientePlanId)] || {};
            // Solo mezclar cache del plan actual (no lineas de otro paquete)
            const next = initLineasDesdePlan(plan.detalle, cached);
            const prevKeys = Object.keys(prev);
            const nextKeys = Object.keys(next);
            const mismoPlanVisible = prevKeys.length > 0
                && prevKeys.every((k) => nextKeys.includes(k) || cached[k]);
            if (
                mismoPlanVisible
                && prevKeys.length === nextKeys.length
                && nextKeys.every((k) =>
                    Number(prev[k]?.cantidad) === Number(next[k]?.cantidad)
                    && String(prev[k]?.id_users || "") === String(next[k]?.id_users || "")
                )
            ) {
                return prev;
            }
            lineasPorPlanRef.current[String(pacientePlanId)] = next;
            return next;
        });
    }, [pacientePlanId, planesPaciente, initLineasDesdePlan]);

    const mapLineasParaApi = (lineasMap, esPendiente) =>
        Object.values(lineasMap || {}).map((l) => ({
            paciente_plan_detalle_id: esPendiente ? null : Number(l.paciente_plan_detalle_id),
            lista_precios_id: l.lista_precios_id || undefined,
            cantidad: Number(l.cantidad) || 0,
            id_users: l.id_users ? Number(l.id_users) : null,
        }));

    /** Arma consumo con todos los paquetes tocados (no solo el chip activo). */
    const construirPlanConsumoMulti = useCallback(() => {
        if (pacientePlanId) {
            lineasPorPlanRef.current[String(pacientePlanId)] = lineas;
        }

        const bloqueActual = planesPaciente.find((b) => String(b.plan?.id) === String(pacientePlanId));
        const esPendienteActual = !!bloqueActual?.plan?._pendiente_asignar;

        // Asignar planes (catálogo pendiente): un solo bloque como antes
        if (esPendienteActual || (!modoGestion && soloPaciente && esPendienteActual)) {
            if (!pacientePlanId) return null;
            return {
                paciente_plan_id: null,
                plan_id_catalogo: Number(bloqueActual?.plan?.plan_id),
                _pendiente_asignar: true,
                lineas: mapLineasParaApi(lineas, true),
            };
        }

        if (modoGestion && esPendienteActual) {
            return null;
        }

        const planesOut = [];
        Object.keys(lineasPorPlanRef.current).forEach((planKey) => {
            if (String(planKey).startsWith('nuevo_')) return;
            const bloque = planesPaciente.find((b) => String(b.plan?.id) === String(planKey));
            if (!bloque || bloque.plan?._pendiente_asignar) return;
            const map = lineasPorPlanRef.current[planKey] || {};
            const lineasArr = mapLineasParaApi(map, false);
            const tieneSesiones = lineasArr.some((l) => Number(l.cantidad) > 0);
            if (!tieneSesiones && String(planKey) !== String(pacientePlanId)) return;
            planesOut.push({
                paciente_plan_id: Number(planKey),
                lineas: lineasArr,
            });
        });

        // Incluir plan actual aunque esté en 0 (para poder limpiar cupos al guardar)
        if (pacientePlanId && !String(pacientePlanId).startsWith('nuevo_')) {
            const ya = planesOut.some((p) => String(p.paciente_plan_id) === String(pacientePlanId));
            if (!ya && !esPendienteActual) {
                planesOut.push({
                    paciente_plan_id: Number(pacientePlanId),
                    lineas: mapLineasParaApi(lineas, false),
                });
            }
        }

        if (!planesOut.length) {
            if (!pacientePlanId || esPendienteActual) return null;
            return {
                paciente_plan_id: Number(pacientePlanId),
                lineas: mapLineasParaApi(lineas, false),
            };
        }

        const primario = planesOut.find((p) => String(p.paciente_plan_id) === String(pacientePlanId))
            || planesOut[0];

        return {
            planes: planesOut,
            paciente_plan_id: primario.paciente_plan_id,
            lineas: primario.lineas,
        };
    }, [pacientePlanId, lineas, planesPaciente, modoGestion, soloPaciente]);

    useEffect(() => {
        if (!pacientePlanId) {
            return;
        }
        const bloque = planesPaciente.find((b) => String(b.plan?.id) === String(pacientePlanId));
        const esPendiente = bloque?.plan?._pendiente_asignar;
        if (modoGestion && esPendiente) {
            return;
        }
        const payload = construirPlanConsumoMulti();
        if (payload) {
            setPlanConsumo(payload);
        }
    }, [modoGestion, pacientePlanId, lineas, planesPaciente, setPlanConsumo, construirPlanConsumoMulti]);

    /** Al cambiar Planes ↔ Asignar: guarda/restaura borrador de cada pestaña (no se pierden sesiones). */
    useEffect(() => {
        if (formSoloLectura) return;
        const modo = soloPaciente ? 'asignar' : (modoGestion ? 'gestion' : null);
        if (!modo) return;

        const prev = modoPestanaRef.current;
        if (prev === modo) return;

        if (prev === 'gestion' || prev === 'asignar') {
            const pendientes = (planesPaciente || []).filter((b) => b.plan?._pendiente_asignar);
            borradorPestanaRef.current[prev] = {
                pacientePlanId,
                lineas: { ...lineas },
                planConsumo: planConsumo || null,
                planNuevoId,
                planesPendientes: pendientes,
            };
        }

        const draft = borradorPestanaRef.current[modo] || {};
        setPacientePlanId(draft.pacientePlanId || '');
        setLineas(draft.lineas || {});
        setPlanNuevoId(draft.planNuevoId || '');
        setMsg('');
        // Restaurar consumo de la pestaña destino; si Asignar está vacío, no borrar el de Planes
        if (modo === 'gestion') {
            setPlanConsumo(draft.planConsumo || null);
        } else if (draft.planConsumo) {
            setPlanConsumo(draft.planConsumo);
        }

        setPlanesPaciente((lista) => {
            const contratados = (lista || []).filter((b) => !b.plan?._pendiente_asignar);
            if (modo === 'asignar') {
                return [...(draft.planesPendientes || []), ...contratados];
            }
            return contratados;
        });

        modoPestanaRef.current = modo;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modoGestion, soloPaciente, formSoloLectura]);

    const seleccionarPaciente = (p) => {
        setIdPaciente(p.id);
        setIdPacienteExistente(p.id);
        setPrimerNombre(p.primer_nombre || "");
        setSegundoNombre(p.segundo_nombre || "");
        setPrimerApellido(p.primer_apellido || "");
        setSegundoApellido(p.segundo_apellido || "");
        setTipoDocumento(p.tipo_documento || "");
        setNumeroDocumento(p.numero_documento || "");
        setCelular(p.celular || p.telefono ? String(p.celular || p.telefono).slice(0, 15) : "");
        setQ("");
        setPacientePlanId("");
        setLineas({});
        lineasPorPlanRef.current = {};
        borradorPestanaRef.current = {
            gestion: { pacientePlanId: '', lineas: {}, planConsumo: null, planNuevoId: '' },
            asignar: { pacientePlanId: '', lineas: {}, planConsumo: null, planNuevoId: '', planesPendientes: [] },
        };
        setConsumoCargado(false);
        setConsumoBloqueado(false);
        setPlanConsumo(null);
    };

    const filtrados = (() => {
        const t = q.trim().toLowerCase();
        if (t.length < 2) return [];
        return (listaPacientes || [])
            .filter((p) => {
                const nom = [p.primer_nombre, p.segundo_nombre, p.primer_apellido, p.segundo_apellido].filter(Boolean).join(" ").toLowerCase();
                const doc = String(p.numero_documento || "").toLowerCase();
                return nom.includes(t) || doc.includes(t);
            })
            .slice(0, 12);
    })();

    const onSelectPlan = (id) => {
        const prevId = pacientePlanId;
        const plan = planesPaciente.find((b) => String(b.plan.id) === String(id));
        const idStr = String(id);

        // Guarda lo que había en el plan actual antes de cambiar
        if (prevId && String(prevId) !== idStr) {
            guardarLineasPlanActual(prevId, lineas);
        }

        setPacientePlanId(id);
        if (!plan?.detalle) {
            setLineas({});
            return;
        }

        const cached = lineasPorPlanRef.current[idStr] || {};
        const next = initLineasDesdePlan(plan.detalle, cached);
        lineasPorPlanRef.current[idStr] = next;
        setLineas(next);
    };

    /** Mientras edita sesiones del plan activo, mantiene la caché al día. */
    useEffect(() => {
        if (!pacientePlanId) return;
        lineasPorPlanRef.current[String(pacientePlanId)] = lineas;
    }, [lineas, pacientePlanId]);

    /** Un solo plan activo → se selecciona solo (menos clics). No aplica en Asignar planes. */
    useEffect(() => {
        if (soloPaciente) return;
        if (!idPaciente || loadingPlanes || deshabilitado) return;
        if (pacientePlanId) return;
        const candidatos = modoGestion
            ? planesActivos.filter((b) => !b.plan?._pendiente_asignar)
            : planesActivos;
        if (candidatos.length !== 1) return;
        onSelectPlan(String(candidatos[0].plan.id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idPaciente, loadingPlanes, planesPaciente, pacientePlanId, modoGestion, soloPaciente, deshabilitado]);

    const updateLinea = (detalleId, field, value) => {
        const key = String(detalleId);
        setLineas((prev) => {
            const det = (planSeleccionado?.detalle || []).find((d) => String(d.id) === key);
            const max = det ? Number(det.sesiones_pendientes) : 0;
            const prevRow = prev[key] || { paciente_plan_detalle_id: detalleId, cantidad: 0, id_users: "" };

            if (field === "cantidad") {
                let n = parseInt(String(value), 10);
                if (Number.isNaN(n) || n < 0) n = 0;
                if (n > max) n = max;
                return {
                    ...prev,
                    [key]: {
                        ...prevRow,
                        cantidad: n,
                        // Si baja a 0, limpia profesional; si sube, no rellena solo
                        id_users: n > 0 ? (prevRow.id_users || "") : "",
                    },
                };
            }

            if (field === "id_users") {
                return {
                    ...prev,
                    [key]: {
                        ...prevRow,
                        id_users: value ? String(value) : "",
                    },
                };
            }

            return {
                ...prev,
                [key]: {
                    ...prevRow,
                    [field]: value,
                },
            };
        });
    };

    const mapDetalleCatalogo = (tempId, detalleCatalogo) =>
        (detalleCatalogo || []).map((d, idx) => {
            const total = Number(d.sesiones_total ?? d.sesiones) || 0;
            return {
                id: `${tempId}_det_${idx}`,
                lista_precios_id: d.lista_precios_id,
                servicio_nombre: d.servicio_nombre || d.nombre || "Servicio",
                sesiones_total: total,
                sesiones_usadas: 0,
                sesiones_pendientes: total,
            };
        });

    const asignarPlanCatalogo = (planIdOverride) => {
        const idPlan = planIdOverride || planNuevoId;
        if (!idPaciente || !idPlan) return;
        setMsg("");

        const planCat = catalogo.find((p) => String(p.id) === String(idPlan));
        if (!planCat) {
            setMsg("Plan no encontrado en el catálogo.");
            return;
        }

        setAsignandoLocal(true);
        setPlanNuevoId(String(idPlan));
        axios
            .get("/plan-agendamiento", { params: { id: idPlan } })
            .then((r) => {
                const detalleSrc = r.data?.detalle || [];
                if (!detalleSrc.length) {
                    setMsg("El plan no tiene servicios configurados.");
                    return;
                }

                const tempId = `nuevo_${Date.now()}`;
                const detalleLocal = mapDetalleCatalogo(tempId, detalleSrc);
                const bloquePlan = {
                    plan: {
                        id: tempId,
                        plan_id: Number(idPlan),
                        plan_nombre: r.data?.plan?.nombre || planCat.nombre,
                        estado: 0,
                        fecha_inicio: ymdLocal(new Date()),
                        _pendiente_asignar: true,
                    },
                    detalle: detalleLocal,
                };

                setPlanesPaciente((prev) => [
                    bloquePlan,
                    ...(prev || []).filter((b) => !b.plan?._pendiente_asignar),
                ]);
                if (pacientePlanId) {
                    guardarLineasPlanActual(pacientePlanId, lineas);
                }
                const nextLineas = initLineasDesdePlan(detalleLocal, {});
                lineasPorPlanRef.current[tempId] = nextLineas;
                setPacientePlanId(tempId);
                setLineas(nextLineas);
                setPlanNuevoId("");
                setMsg("Plan listo. Ajuste sesiones y profesional si aplica, luego agende.");
            })
            .catch(() => setMsg("No se pudo cargar el detalle del plan."))
            .finally(() => setAsignandoLocal(false));
    };

    const quitarPlanPendienteAsignar = () => {
        const idQuitar = pacientePlanId;
        const bloque = planesPaciente.find((b) => String(b.plan?.id) === String(idQuitar));
        if (!bloque?.plan?._pendiente_asignar) return;

        setPlanesPaciente((prev) => (prev || []).filter((b) => !b.plan?._pendiente_asignar));
        setPacientePlanId("");
        setLineas({});
        setPlanNuevoId("");
        setMsg("");
        setPlanConsumo(null);
        if (idQuitar) {
            delete lineasPorPlanRef.current[String(idQuitar)];
        }
        borradorPestanaRef.current.asignar = {
            pacientePlanId: '',
            lineas: {},
            planConsumo: null,
            planNuevoId: '',
            planesPendientes: [],
        };
    };

    const etiquetaPlanContratado = (plan) => {
        const nombre = plan?.plan_nombre || "Plan";
        if (plan?._pendiente_asignar) {
            return `${nombre} — Pendiente de asignar`;
        }
        const fecha = plan?.fecha_inicio ? formatearFechaDisplay(plan.fecha_inicio) : "";
        if (fecha && fecha !== "—") {
            return `${nombre} — Contratado ${fecha}`;
        }
        return `${nombre} — Contratado`;
    };

    const nombreUsuarioPorId = (userId) => {
        if (!userId) return "";
        const u = (usuariosProfesionales || []).find((item) => String(item.id) === String(userId));
        if (!u) return "";
        return String(u.name || "").trim();
    };

    const nombreProfesionalLinea = (ln) => {
        if (ln.profesional_nombre) return ln.profesional_nombre;
        const porId = nombreUsuarioPorId(ln.id_users);
        if (porId) return porId;
        return "—";
    };

    const bannerSoloLectura = motivoSoloLecturaCita === 'atendida' ? (
        <div className="alert alert-info py-2 mb-3 text-center cita-bloqueo-alert" role="alert">
            <i className="fas fa-lock mr-2"></i>
            ESTA CITA ESTÁ EN ESTADO <strong>ATENDIDA</strong> Y NO PUEDE MODIFICARSE.
        </div>
    ) : motivoSoloLecturaCita === 'no_cumplida' ? (
        <div className="alert alert-danger py-2 mb-3 text-center cita-bloqueo-alert" role="alert">
            <i className="fas fa-lock mr-2"></i>
            ESTA CITA ESTÁ EN ESTADO <strong>NO CUMPLIDA</strong> Y NO PUEDE MODIFICARSE.
        </div>
    ) : null;

    const renderCamposPaciente = () => (
        <>
            {!verCita && (
                <div className="form-group mb-2">
                    <label className="small mb-1">Paciente *</label>
                    <input
                        className="form-control form-control-sm"
                        placeholder="Escriba nombre o documento y elija…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        disabled={deshabilitado}
                        autoFocus={!idPaciente}
                    />
                    {filtrados.length > 0 && (
                        <div className="list-group mt-1 fcpp-paciente-sugerencias">
                            {filtrados.map((p) => (
                                <button
                                    type="button"
                                    key={p.id}
                                    className="list-group-item list-group-item-action py-1 small"
                                    onClick={() => seleccionarPaciente(p)}
                                >
                                    {[p.primer_nombre, p.primer_apellido].filter(Boolean).join(" ")} — {p.tipo_documento} {p.numero_documento}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {idPaciente ? (
                <div className="fcpp-paciente-seleccionado d-flex justify-content-between align-items-center mb-2 py-2 px-3 rounded small">
                    <div className="d-flex align-items-center min-width-0">
                        <i className="fas fa-user-check mr-2 fcpp-paciente-seleccionado__icon" aria-hidden="true"></i>
                        <div className="text-truncate">
                            <strong className="fcpp-paciente-seleccionado__nombre">{nombreCompleto || "Paciente"}</strong>
                            <span className="fcpp-paciente-seleccionado__doc ml-2">{tipoDocumento} {numeroDocumento}</span>
                        </div>
                    </div>
                    {!verCita && !deshabilitado && (
                        <button
                            type="button"
                            className="btn btn-link btn-sm fcpp-paciente-seleccionado__link p-0 ml-2"
                            onClick={() => {
                                setIdPaciente("");
                                setIdPacienteExistente("-1");
                                setPacientePlanId("");
                                setLineas({});
                                lineasPorPlanRef.current = {};
                                setQ("");
                            }}
                            title="Cambiar paciente"
                        >
                            Cambiar
                        </button>
                    )}
                </div>
            ) : (
                <p className="text-muted small mb-2">Busque y seleccione el paciente.</p>
            )}
        </>
    );

    const renderCamposCita = () => (
        <>
            {renderCamposPaciente()}

            {!soloPaciente && (
            <div className="fcpp-cita-grid">
                <div className="form-group mb-2 fcpp-cita-grid__fecha">
                    <label className="small mb-0">Fecha *</label>
                    <input
                        type="date"
                        className={`form-control form-control-sm ${hayErrorCampo("fecha_cita") ? "is-invalid" : ""}`}
                        value={normalizeFechaInput(fechaCita)}
                        onChange={(e) => setFechaCita(normalizeFechaInput(e.target.value))}
                        disabled={deshabilitado}
                    />
                </div>
                <div className="form-group mb-2 fcpp-cita-grid__hora">
                    <label className="small mb-0">Hora *</label>
                    <div className="d-flex fcpp-hora-group">
                        <select className="form-control form-control-sm" value={horaAtencionCita} onChange={(e) => setHoraAtencionCita(e.target.value)} disabled={deshabilitado}>
                            <option value="">H</option>
                            {["00","01","02","03","04","05","06","07","08","09","10","11","12"].map((h) => (
                                <option key={h} value={h}>{h}</option>
                            ))}
                        </select>
                        <select className="form-control form-control-sm" value={minutoCita} onChange={(e) => setMinutoCita(e.target.value)} disabled={deshabilitado}>
                            <option value="">M</option>
                            {["00","05","10","15","20","25","30","35","40","45","50","55"].map((m) => (
                                <option key={m} value={m}>{m}</option>
                            ))}
                        </select>
                        <select className="form-control form-control-sm" value={ampmCita} onChange={(e) => setAmpmCita(e.target.value)} disabled={deshabilitado}>
                            <option value=""> </option>
                            <option value="am">AM</option>
                            <option value="pm">PM</option>
                        </select>
                    </div>
                </div>
                <div className="form-group mb-2 fcpp-cita-grid__agenda">
                    <label className="small mb-0">Agenda *</label>
                    <select
                        className={`form-control form-control-sm ${hayErrorCampo("id_users") ? "is-invalid" : ""}`}
                        value={idUsers || ""}
                        onChange={(e) => setIdUsers(e.target.value)}
                        disabled={deshabilitado || readOnlyProfesional}
                    >
                        <option value=""> </option>
                        {(agendas || []).map((a) => (
                            <option key={a.id} value={a.id}>{a.nombre}</option>
                        ))}
                    </select>
                </div>
                <div className="form-group mb-2 fcpp-cita-grid__duracion">
                    <label className="small mb-0">Duración *</label>
                    <input
                        type="number"
                        min={stepDuracionCita}
                        step={stepDuracionCita}
                        className={`form-control form-control-sm ${hayErrorCampo("duracion_cita") ? "is-invalid" : ""}`}
                        value={duracionCita}
                        onChange={(e) => setDuracionCita(e.target.value)}
                        disabled={deshabilitado}
                    />
                </div>
                <div className="form-group mb-2 fcpp-cita-grid__estado">
                    <label className="small mb-0">Estado *</label>
                    <select
                        className={`form-control form-control-sm ${hayErrorCampo("estado_cita") ? "is-invalid" : ""}`}
                        value={estadoCita}
                        onChange={(e) => handleEstadoCitaChange(e.target.value)}
                        disabled={deshabilitado || readOnlyEstadoCita}
                    >
                        <option value=""></option>
                        {(estadosCita || []).map((estado) => (
                            <option key={estado.id} value={estado.id}>{estado.nombre}</option>
                        ))}
                    </select>
                </div>
                <div className="form-group mb-2 fcpp-cita-grid__motivo">
                    <label className="small mb-0">Motivo</label>
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        value={motivoConsulta}
                        onChange={(e) => setMotivoConsulta(e.target.value)}
                        placeholder="Opcional…"
                        disabled={deshabilitado}
                    />
                </div>
            </div>
            )}
        </>
    );

    const ajustarSesion = (detalleId, delta, max) => {
        const key = String(detalleId);
        const actual = Number(lineas[key]?.cantidad) || 0;
        let next = actual + delta;
        if (next < 0) next = 0;
        if (next > max) next = max;
        updateLinea(detalleId, "cantidad", next);
    };

    const renderTablaSesiones = () => {
        if (!planSeleccionado) return null;
        return (
            <>
                <div className="d-flex justify-content-between align-items-center mb-1">
                    <strong className="small">Sesiones de esta cita</strong>
                    {consumoBloqueado && (
                        <span className="badge badge-secondary">Consumo aplicado</span>
                    )}
                </div>
                <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-0 small fcpp-sesiones-table">
                        <thead className="thead-light">
                            <tr>
                                <th>Servicio</th>
                                <th className="text-center" title="Pendientes / Total">Cupo</th>
                                <th className="text-center">Sesiones</th>
                                <th>Profesional</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(planSeleccionado.detalle || []).map((d) => {
                                const key = String(d.id);
                                const row = lineas[key] || { cantidad: 0, id_users: "" };
                                const max = Number(d.sesiones_pendientes) || 0;
                                const usadas = Number(d.sesiones_usadas) || 0;
                                const total = Number(d.sesiones_total) || 0;
                                return (
                                    <tr key={d.id}>
                                        <td className="align-middle">{d.servicio_nombre}</td>
                                        <td className="align-middle text-center text-muted small">
                                            {max}/{total}
                                            {usadas > 0 && <div className="text-muted" style={{ fontSize: "0.7rem" }}>usadas {usadas}</div>}
                                        </td>
                                        <td className="align-middle p-1 text-center">
                                            <div className="fcpp-sesion-stepper">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary"
                                                    disabled={deshabilitado || max < 1 || Number(row.cantidad) < 1}
                                                    onClick={() => ajustarSesion(d.id, -1, max)}
                                                    aria-label="Menos"
                                                >
                                                    −
                                                </button>
                                                <span className="fcpp-sesion-stepper__val">{row.cantidad}</span>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary"
                                                    disabled={deshabilitado || max < 1 || Number(row.cantidad) >= max}
                                                    onClick={() => ajustarSesion(d.id, 1, max)}
                                                    aria-label="Más"
                                                >
                                                    +
                                                </button>
                                            </div>
                                        </td>
                                        <td className="align-middle p-1">
                                            <select
                                                className="form-control form-control-sm"
                                                value={row.id_users || ""}
                                                onChange={(e) => updateLinea(d.id, "id_users", e.target.value)}
                                                disabled={deshabilitado || Number(row.cantidad) < 1}
                                            >
                                                <option value="">{Number(row.cantidad) > 0 ? "Profesional…" : "—"}</option>
                                                {usuariosProfesionales.map((u) => (
                                                    <option key={u.id} value={u.id}>{u.name}</option>
                                                ))}
                                            </select>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </>
        );
    };

    const planTieneSesionesEnCita = (planId) => {
        const key = String(planId);
        const map = (String(pacientePlanId) === key)
            ? lineas
            : (lineasPorPlanRef.current[key] || {});
        return Object.values(map).some((l) => Number(l?.cantidad) > 0);
    };

    const renderSelectorPlanes = (lista, etiqueta) => {
        if (!lista.length) return null;
        const unico = lista.length === 1 ? lista[0] : null;
        const multi = lista.length > 1;
        return (
            <div className="form-group mb-2">
                <label className="small font-weight-bold mb-1">{etiqueta}</label>
                {multi && (
                    <p className="text-muted small mb-1">
                        {deshabilitado
                            ? "Elija cada paquete para consultar las sesiones de esta cita."
                            : "Puede usar varios paquetes en la misma cita: elija cada uno y asigne sesiones."}
                    </p>
                )}
                {unico ? (
                    <div className="fcpp-plan-unico small mb-0">
                        <i className="fas fa-check-circle text-info mr-1" aria-hidden="true"></i>
                        <strong>{unico.plan.plan_nombre || "Plan"}</strong>
                        {unico.plan._pendiente_asignar && <span className="text-muted"> (nuevo)</span>}
                        {unico.plan.fecha_inicio && !unico.plan._pendiente_asignar && (
                            <span className="text-muted"> · desde {formatearFechaDisplay(unico.plan.fecha_inicio)}</span>
                        )}
                    </div>
                ) : lista.length <= 4 ? (
                    <div className="fcpp-plan-chips mb-0">
                        {lista.map((b) => {
                            const activo = String(b.plan.id) === String(pacientePlanId);
                            const conSesiones = planTieneSesionesEnCita(b.plan.id);
                            return (
                                <button
                                    type="button"
                                    key={b.plan.id}
                                    className={`btn btn-sm fcpp-plan-chip ${activo ? "fcpp-plan-chip--active" : "btn-outline-secondary"}${conSesiones && !activo ? " fcpp-plan-chip--con-sesiones" : ""}`}
                                    onClick={() => onSelectPlan(String(b.plan.id))}
                                    title={conSesiones ? "Este paquete ya tiene sesiones en esta cita" : (deshabilitado ? "Consultar este paquete" : undefined)}
                                >
                                    {b.plan.plan_nombre || "Plan"}
                                    {b.plan._pendiente_asignar ? " (nuevo)" : ""}
                                    {conSesiones ? " ·" : ""}
                                </button>
                            );
                        })}
                    </div>
                ) : (
                    <select
                        className="form-control form-control-sm"
                        value={pacientePlanId}
                        onChange={(e) => onSelectPlan(e.target.value)}
                    >
                        <option value="">Seleccione…</option>
                        {lista.map((b) => (
                            <option key={b.plan.id} value={b.plan.id}>
                                {etiquetaPlanContratado(b.plan)}
                                {planTieneSesionesEnCita(b.plan.id) ? " (con sesiones)" : ""}
                            </option>
                        ))}
                    </select>
                )}
            </div>
        );
    };

    const renderSeccionPlanes = () => {
        if (!idPaciente) {
            return <p className="text-muted small mt-2 mb-0">Seleccione un paciente.</p>;
        }

        return (
            <>
                <fieldset disabled={!idPaciente || deshabilitado} style={{ border: 0, margin: 0, padding: 0 }}>
                    <div className="form-group mb-2">
                        <label className="small font-weight-bold mb-1">Plan del catálogo</label>
                        <select
                            className="form-control form-control-sm"
                            value={planNuevoId}
                            onChange={(e) => {
                                const v = e.target.value;
                                setPlanNuevoId(v);
                                if (v) asignarPlanCatalogo(v);
                            }}
                            disabled={asignandoLocal}
                        >
                            <option value="">{asignandoLocal ? "Cargando…" : "Elegir y asignar…"}</option>
                            {catalogo.map((p) => (
                                <option key={p.id} value={p.id}>{p.nombre}</option>
                            ))}
                        </select>
                    </div>
                </fieldset>

                {planSeleccionado?.plan?._pendiente_asignar && (
                    <div className="fcpp-plan-unico small mb-2 d-flex align-items-center justify-content-between flex-wrap">
                        <div className="min-width-0 mr-2">
                            <i className="fas fa-check-circle text-info mr-1" aria-hidden="true"></i>
                            <strong>{planSeleccionado.plan.plan_nombre || "Plan"}</strong>
                            <span className="text-muted"> — configure las sesiones de esta cita</span>
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-danger py-0 px-2"
                            onClick={quitarPlanPendienteAsignar}
                            disabled={deshabilitado || asignandoLocal}
                            title="Quitar plan seleccionado"
                        >
                            <i className="fas fa-times mr-1" aria-hidden="true"></i>
                            Quitar
                        </button>
                    </div>
                )}

                {planSeleccionado?.plan?._pendiente_asignar ? renderTablaSesiones() : null}
                {msg && <div className="alert alert-info py-1 mt-2 mb-0 small">{msg}</div>}
                {errorlist?.plan_consumo && (
                    <div className="text-danger small mt-1">{textoError(errorlist.plan_consumo)}</div>
                )}
            </>
        );
    };

    /** Pestaña "Planes": solo planes activos ya contratados (la cita se gestiona en Cita). */
    const renderSeccionPlanesActivos = () => {
        if (!idPaciente) {
            return (
                <p className="text-muted small mt-2 mb-0">
                    Seleccione el paciente en la pestaña <strong>Cita</strong> para ver sus planes activos.
                </p>
            );
        }

        const planesContratados = planesActivos.filter((b) => !b.plan?._pendiente_asignar);

        return (
            <>
                {loadingPlanes ? (
                    <p className="text-muted small">
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Obteniendo planes…
                    </p>
                ) : planesContratados.length < 1 ? (
                    <span className="badge badge-secondary d-inline-block">
                        Sin planes activos. Use la pestaña Asignar planes.
                    </span>
                ) : (
                    renderSelectorPlanes(
                        planesContratados,
                        planesContratados.length > 1 ? "Planes en esta cita" : "Plan activo"
                    )
                )}

                {renderTablaSesiones()}
                {errorlist?.plan_consumo && (
                    <div className="text-danger small mt-1">{textoError(errorlist.plan_consumo)}</div>
                )}
            </>
        );
    };

    if (formSoloLectura) {
        const lineasCita = consumoDetalle?.lineas || [];
        const nombresPlanesReadonly = (() => {
            const seen = new Set();
            const out = [];
            lineasCita.forEach((ln) => {
                const nom = ln.plan_nombre || consumoDetalle?.plan_nombre;
                if (!nom || seen.has(nom)) return;
                seen.add(nom);
                out.push(nom);
            });
            if (!out.length && consumoDetalle?.plan_nombre) out.push(consumoDetalle.plan_nombre);
            return out;
        })();
        return (
            <div className="form-cita-planes-panel">
                {bannerSoloLectura}
                {!consumoConsultaTerminada ? (
                    <p className="text-muted small mb-0">
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Obteniendo información…
                    </p>
                ) : consumoDetalle ? (
                    <>
                        {nombresPlanesReadonly.length > 0 && (
                            <div className="mb-3">
                                <div className="small text-muted mb-1">
                                    {nombresPlanesReadonly.length > 1 ? "Planes en esta cita" : "Plan en esta cita"}
                                </div>
                                <div className="fcpp-descontado-planes">
                                    {nombresPlanesReadonly.map((nom) => (
                                        <span key={nom} className="badge badge-info mr-1 mb-1">{nom}</span>
                                    ))}
                                </div>
                            </div>
                        )}
                        {lineasCita.length > 0 ? (
                            <div className="fcpp-descontado-cita">
                                <strong className="d-block mb-3 fcpp-descontado-cita__titulo">
                                    {consumoDetalle.bloqueado ? "Descontado en esta cita" : "Reservado en esta cita"}
                                </strong>
                                <ul className="list-unstyled mb-0 fcpp-descontado-cita__lista">
                                    {lineasCita.map((ln) => (
                                        <li
                                            key={`${ln.paciente_plan_id || ''}_${ln.paciente_plan_detalle_id}`}
                                            className="fcpp-descontado-cita__item"
                                        >
                                            <i className={`fas ${esConsumoActivo(ln.estado) ? "fa-check-circle text-success" : "fa-clock text-warning"} mr-2`} aria-hidden="true"></i>
                                            <span className="fcpp-descontado-cita__texto">
                                                {ln.plan_nombre ? (
                                                    <span className="fcpp-descontado-cita__plan">{ln.plan_nombre}</span>
                                                ) : null}
                                                {ln.plan_nombre ? ": " : null}
                                                <strong>{ln.servicio_nombre || "Servicio"}</strong>
                                                <span className="text-muted"> × {ln.cantidad}</span>
                                                {nombreProfesionalLinea(ln) !== "—" && (
                                                    <span className="text-muted"> · {nombreProfesionalLinea(ln)}</span>
                                                )}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : (
                            <p className="text-muted mb-0">Sin sesiones asociadas a esta cita.</p>
                        )}
                        <p className="text-muted small mt-3 mb-0">
                            {consumoDetalle.bloqueado
                                ? "El consumo ya fue aplicado al marcar la cita como Atendida."
                                : "Reserva pendiente de aplicar."}
                        </p>
                    </>
                ) : (
                    <p className="text-muted mb-0">No tiene planes activos en esta cita.</p>
                )}
            </div>
        );
    }

    if (modoGestion) {
        return (
            <div className="form-cita-planes-panel">
                {bannerSoloLectura}
                {renderSeccionPlanesActivos()}
            </div>
        );
    }

    return (
        <div className="form-cita-planes-panel">
            {bannerSoloLectura}
            <div className="mb-2">
                {soloPaciente ? renderCamposPaciente() : renderCamposCita()}
            </div>
            <div className="border-top pt-2">
                {renderSeccionPlanes()}
            </div>
        </div>
    );
}

export default FormCitaPlanesPanel;
